import { LOCALE } from '@constants/locale';

export const PLUSPOINT_INTEGRATION = 4;

const GOOGLE_OAUTH_BASE = 'https://accounts.google.com/o/oauth2/v2/auth';

const buildGoogleOAuthUrl = (redirectUri, scope, options = {}) => {
    const params = new URLSearchParams({
        client_id: import.meta.env.REACT_APP_GOOGLE_CLIENT_ID || '',
        redirect_uri: `${window.location.origin}${redirectUri}`,
        response_type: 'code',
        scope: Array.isArray(scope) ? scope.join(' ') : scope,
        access_type: 'offline',
        include_granted_scopes: 'true',
        state:
            typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : Math.random().toString(36).slice(2),
    });
    if (options.promptConsent) {
        params.set('prompt', 'consent');
    }
    return `${GOOGLE_OAUTH_BASE}?${params.toString()}`;
};

const SIGN_IN_SCOPES = [
    'openid',
    'https://www.googleapis.com/auth/userinfo.profile',
    'https://www.googleapis.com/auth/userinfo.email',
];

const SIGN_UP_SCOPES = [
    ...SIGN_IN_SCOPES,
    'https://www.googleapis.com/auth/business.manage',
    'https://www.googleapis.com/auth/businesscommunications',
];

const INTEGRATIONS_SCOPES = [
    'https://www.googleapis.com/auth/business.manage',
    'https://www.googleapis.com/auth/businesscommunications',
    'https://www.googleapis.com/auth/userinfo.email',
];

export const getGoogleOAuthSignInUrl = () => buildGoogleOAuthUrl('/login', SIGN_IN_SCOPES);

export const getGoogleOAuthSignUpUrl = () => buildGoogleOAuthUrl('/signup', SIGN_UP_SCOPES, { promptConsent: true });

export const getGoogleOAuthAcceptInvitationUrl = () => buildGoogleOAuthUrl('/accept-invitation', SIGN_IN_SCOPES);

export const getGoogleOAuthUrl = () =>
    buildGoogleOAuthUrl('/account-settings/integrations', INTEGRATIONS_SCOPES, { promptConsent: true });

export const getGoogleOnboardingOAuthUrl = () =>
    buildGoogleOAuthUrl('/onboarding', INTEGRATIONS_SCOPES, { promptConsent: true });

export const ZAPIER_URL = 'https://zapier.com/apps/pluspoint/integrations';

export const ProfileType = {
    1: 'Yelp',
    2: 'Facebook',
    3: 'Google',
    8: 'Trustpilot',
    9: 'TripAdvisor',
    12: 'Instagram',
    14: 'TheFork',
    15: 'Deliveroo',
    16: 'Booking.com',
    24: 'Youtube',
    25: 'X',
    26: 'LinkedIn',
    27: 'Pinterest',
    28: 'Tiktok',
    29: 'Hotels.com',
    30: 'Expedia',
    32: 'Healthgrades',
    33: 'Zocdoc',
    34: 'RateMDs',
    37: 'Uber Eats',
    38: 'BBB',
    // INFO: Profiles created via Location integrations
    10: 'Google',
    20: 'Facebook',
    13: 'Instagram',
    19: 'CoverManager',
    17: 'iFood',
    18: 'VTEX',
    36: 'Yext',
};

export const ProfileName = {
    Yelp: 1,
    Facebook: 2,
    Google: 3,
    Trustpilot: 8,
    TripAdvisor: 9,
    Instagram: 12,
    TheFork: 14,
    Deliveroo: 15,
    'Booking.com': 16,
    YouTube: 24,
    X: 25,
    LinkedIn: 26,
    Pinterest: 27,
    Tiktok: 28,
    'Hotels.com': 29,
    Expedia: 30,
    Healthgrades: 32,
    Zocdoc: 33,
    RateMDs: 34,
    UberEats: 37,
    BBB: 38,
    // INFO: Profiles created via Location integrations
    GoogleOfficial: 10,
    FacebookOfficial: 20,
    InstagramOfficial: 13,
    CoverManager: 19,
    iFood: 17,
    VTEX: 18,
    Yext: 36,
};

// INFO: Dict for Profiles section in Location settings.
// Display-only social platforms (Instagram, YouTube, X, LinkedIn, Pinterest, TikTok) are intentionally
// NOT here — they live on the location's social links, not as profiles. Facebook stays (review platform).
export const LocationProfilesDictionary = [
    { name: 'Google', type: ProfileName.Google },
    { name: 'Facebook', type: ProfileName.Facebook },
    { name: 'Trustpilot', type: ProfileName.Trustpilot },
    { name: 'Yelp', type: ProfileName.Yelp },
    { name: 'BBB', type: ProfileName.BBB },
    { name: 'TripAdvisor', type: ProfileName.TripAdvisor },
    { name: 'TheFork', type: ProfileName.TheFork },
    { name: 'Deliveroo', type: ProfileName.Deliveroo },
    { name: 'Uber Eats', type: ProfileName.UberEats },
    { name: 'Booking.com', type: ProfileName['Booking.com'] },
    { name: 'Expedia / Hotels.com', type: ProfileName.Expedia },
    { name: 'Healthgrades', type: ProfileName.Healthgrades },
    { name: 'Zocdoc', type: ProfileName.Zocdoc },
    { name: 'RateMDs', type: ProfileName.RateMDs },
];

// Display-only social platforms — managed via Location.SocialLinks, never listed in the Profiles section.
export const SocialDisplayProfiles = [
    ProfileName.Instagram,
    ProfileName.InstagramOfficial,
    ProfileName.X,
    ProfileName.YouTube,
    ProfileName.LinkedIn,
    ProfileName.Pinterest,
    ProfileName.Tiktok,
];

const ExpediaGroupProfileTypes = [ProfileName.Expedia, ProfileName['Hotels.com']];

export const toLocationProfileType = type => (ExpediaGroupProfileTypes.includes(type) ? ProfileName.Expedia : type);

// An official (OAuth-connected) integration writes its own profile type, distinct from the manual
// review-platform listing type: FacebookOfficial covers Facebook + Instagram, GoogleOfficial covers Google.
// A connected official profile makes the corresponding manual listing redundant.
export const isListingCoveredByOfficial = (profiles, type) => {
    const hasOfficial = officialType => (profiles || []).some(p => p.type === officialType);
    if (type === ProfileName.Google) return hasOfficial(ProfileName.GoogleOfficial);
    if (type === ProfileName.Facebook || type === ProfileName.Instagram)
        return hasOfficial(ProfileName.FacebookOfficial);
    return false;
};

export const LocationIntegrationName = {
    Google: 0,
    Facebook: 1,
    CoverManager: 2,
    iFood: 4,
    VTEX: 5,
    ListingDirectories: 6,
    Tiktok: 7,
    X: 8,
    LinkedIn: 9,
    Square: 10,
    BookingsFeed: 11,
};

export const LocationIntegrationType = {
    0: 'Google',
    1: 'Facebook',
    2: 'CoverManager',
    4: 'iFood',
    5: 'VTEX',
    6: 'ListingDirectories',
    7: 'Tiktok',
    8: 'X',
    9: 'LinkedIn',
    10: 'Square',
    11: 'BookingsFeed',
};

export const AccIntegrationName = {
    Zapier: 11,
    Google: 10,
    Facebook: 20,
    WhatsApp: 35,
    TikTok: 28,
    X: 25,
    LinkedIn: 26,
    'API integration': 0,
    iFood: 17,
    CoverManager: 19,
    VTEX: 18,
    Square: 39,
    RingCentral: 40,
    BookingsFeed: 41,
    Salesforce: 1,
    Xero: 2,
    Pipedrive: 3,
    Hubspot: 4,
    QuickBooks: 5,
};

// INFO: How an integration is connected from the Add-integration modal.
export const IntegrationSetupType = {
    OAuth: 'oauth',
    CredentialModal: 'credentialModal',
    CopyPaste: 'copyPaste',
    Zapier: 'zapier',
    ApiDocs: 'apiDocs',
    ContactSales: 'contactSales',
};

export const IntegrationCategory = {
    Reviews: 'reviews',
    Social: 'social',
    Messaging: 'messaging',
    Delivery: 'delivery',
    Commerce: 'commerce',
    Crm: 'crm',
    Finance: 'finance',
    Developer: 'developer',
};

// INFO: Dict for Account Integrations page
export const AccountIntegrationsDictionary = [
    {
        id: AccIntegrationName.Zapier,
        name: 'Zapier',
        category: IntegrationCategory.Developer,
        setupType: IntegrationSetupType.Zapier,
    },
    {
        id: AccIntegrationName.Google,
        name: 'Google',
        category: IntegrationCategory.Reviews,
        setupType: IntegrationSetupType.OAuth,
        supportsMultiple: true,
    },
    {
        id: AccIntegrationName.Facebook,
        name: 'Facebook',
        category: IntegrationCategory.Social,
        setupType: IntegrationSetupType.OAuth,
        supportsMultiple: true,
    },
    {
        id: AccIntegrationName.WhatsApp,
        name: 'WhatsApp',
        category: IntegrationCategory.Messaging,
        setupType: IntegrationSetupType.OAuth,
        supportsMultiple: true,
    },
    {
        id: AccIntegrationName.TikTok,
        name: 'TikTok',
        category: IntegrationCategory.Social,
        setupType: IntegrationSetupType.OAuth,
        supportsMultiple: true,
    },
    {
        id: AccIntegrationName.X,
        name: 'X',
        category: IntegrationCategory.Social,
        setupType: IntegrationSetupType.OAuth,
        supportsMultiple: true,
    },
    {
        id: AccIntegrationName.LinkedIn,
        name: 'LinkedIn',
        category: IntegrationCategory.Social,
        setupType: IntegrationSetupType.OAuth,
        supportsMultiple: true,
    },
    {
        id: AccIntegrationName['API integration'],
        name: 'API integration',
        category: IntegrationCategory.Developer,
        setupType: IntegrationSetupType.ApiDocs,
    },
    {
        id: AccIntegrationName.iFood,
        name: 'iFood',
        category: IntegrationCategory.Delivery,
        setupType: IntegrationSetupType.CredentialModal,
        locale: LOCALE.BRAZIL,
    },
    {
        id: AccIntegrationName.CoverManager,
        name: 'CoverManager',
        category: IntegrationCategory.Delivery,
        setupType: IntegrationSetupType.CredentialModal,
        locale: LOCALE.SPAIN,
    },
    {
        id: AccIntegrationName.VTEX,
        name: 'VTEX',
        category: IntegrationCategory.Commerce,
        setupType: IntegrationSetupType.CredentialModal,
    },
    {
        id: AccIntegrationName.Square,
        name: 'Square',
        category: IntegrationCategory.Commerce,
        setupType: IntegrationSetupType.OAuth,
        supportsMultiple: true,
    },
    {
        id: AccIntegrationName.RingCentral,
        name: 'RingCentral',
        category: IntegrationCategory.Messaging,
        setupType: IntegrationSetupType.OAuth,
        supportsMultiple: true,
    },
    {
        id: AccIntegrationName.Salesforce,
        name: 'Salesforce',
        category: IntegrationCategory.Crm,
        setupType: IntegrationSetupType.ContactSales,
    },
    {
        id: AccIntegrationName.Xero,
        name: 'Xero',
        category: IntegrationCategory.Finance,
        setupType: IntegrationSetupType.ContactSales,
    },
    {
        id: AccIntegrationName.Pipedrive,
        name: 'Pipedrive',
        category: IntegrationCategory.Crm,
        setupType: IntegrationSetupType.ContactSales,
    },
    {
        id: AccIntegrationName.Hubspot,
        name: 'Hubspot',
        category: IntegrationCategory.Crm,
        setupType: IntegrationSetupType.ContactSales,
    },
    {
        id: AccIntegrationName.QuickBooks,
        name: 'QuickBooks',
        category: IntegrationCategory.Finance,
        setupType: IntegrationSetupType.ContactSales,
    },
];

export const isAccountIntegrationAvailable = (item, account) => {
    if (item.locale && account?.locale !== item.locale) return false;
    if (item.flag && !account?.[item.flag]) return false;
    return true;
};

export const TwoStepIntegrationConfig = {
    [AccIntegrationName.Google]: {
        locationIntegration: LocationIntegrationName.Google,
        listProfiles: true,
        supportsCreate: true,
        groupByAccount: true,
    },
    [AccIntegrationName.Facebook]: {
        locationIntegration: LocationIntegrationName.Facebook,
        listProfiles: true,
        supportsCreate: false,
    },
    [AccIntegrationName.CoverManager]: {
        locationIntegration: LocationIntegrationName.CoverManager,
        listProfiles: true,
        supportsCreate: false,
    },
    [AccIntegrationName.VTEX]: {
        locationIntegration: LocationIntegrationName.VTEX,
        listProfiles: true,
        supportsCreate: false,
    },
    [AccIntegrationName.TikTok]: {
        locationIntegration: LocationIntegrationName.Tiktok,
        listProfiles: false,
        supportsCreate: false,
    },
    [AccIntegrationName.X]: {
        locationIntegration: LocationIntegrationName.X,
        listProfiles: false,
        supportsCreate: false,
        previewService: 'x',
    },
    [AccIntegrationName.LinkedIn]: {
        locationIntegration: LocationIntegrationName.LinkedIn,
        listProfiles: true,
        supportsCreate: false,
    },
    [AccIntegrationName.Square]: {
        locationIntegration: LocationIntegrationName.Square,
        listProfiles: true,
        supportsCreate: false,
    },
};

// INFO: Integrations and Profiles used in Manage reviews, Analytics reviews, Widgets
export const FiltersIntegrationName = {
    Yelp: 1,
    Facebook: 2,
    Google: 3,
    Trustpilot: 8,
    TripAdvisor: 9,
    TheFork: 14,
    Deliveroo: 15,
    'Booking.com': 16,
    iFood: 17,
    'Hotels.com': 29,
    Expedia: 30,
    Healthgrades: 32,
    Zocdoc: 33,
    RateMDs: 34,
    UberEats: 37,
    BBB: 38,
};

export const FiltersIntegrationType = {
    1: 'Yelp',
    2: 'Facebook',
    3: 'Google',
    8: 'Trustpilot',
    9: 'TripAdvisor',
    14: 'TheFork',
    15: 'Deliveroo',
    16: 'Booking.com',
    17: 'iFood',
    29: 'Hotels.com',
    30: 'Expedia',
    32: 'Healthgrades',
    33: 'Zocdoc',
    34: 'RateMDs',
    37: 'Uber Eats',
    38: 'BBB',
};
